// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { Translate } from '@plesk/ui-library';
import { Dialog } from 'common/components/Dialog/Dialog';
import { SIZE } from 'common/constants';
import * as computeResourceActions from 'admin/computeResource/actions';
import { COMPUTE_RESOURCE_DIALOG } from 'admin/computeResource/constants';
import { initialComputeResourceState } from 'admin/computeResource/reducers';
import ComputeResource from 'admin/computeResource/list/containers/ComputeResource';
import ComputeResourceEditForm from 'admin/computeResource/containers/ComputeResourceEditForm/ComputeResourceEditForm';
import SettingsDialog from 'admin/computeResource/page/containers/dialogs/SettingsDialog/SettingsDialog';
import { IComputeResourceResponse } from 'common/api/resources/ComputeResource';

interface IUseComputeResourceDialogsOptions {
    /** Source list to resolve the selected CR by id. */
    items: IComputeResourceResponse[];
    /** Called after remove / on dialog close so the caller can refresh its list. */
    onChanged?: () => void | Promise<void>;
    /**
     * Mirror the opened CR into `state.computeResource.item` and fetch its details when the
     * view dialog opens. Needed by the CR list page, whose create / view dialog renders
     * ComputeResourceInstall and ComputeResourceConfigureNetwork — both read the selected CR
     * from the store rather than from props. Pages that already own `state.computeResource.item`
     * (the individual CR page) must leave this off so they don't clobber it.
     */
    syncSelectionToStore?: boolean;
}

interface IUseComputeResourceDialogsResult {
    handleEdit: (id: number) => void;
    handleSettings: (id: number) => void;
    handleDetailsClick: (id: number) => void;
    handleRemove: (id: number) => Promise<void>;
    handleCreate: () => void;
    handleClose: () => void;
    dialogs: JSX.Element;
}

/**
 * Single owner of the dialog state, handlers, and rendering for the Compute Resource
 * create / view / edit / settings flows. Used by the CR list page, the individual CR
 * page, and the Storage detail page — keep it that way: the three pages previously kept
 * their own copies, and the resulting drift is what broke the CR page's edit dialog
 * (SVM2-7365).
 */
export const useComputeResourceDialogs = ({
    items,
    onChanged,
    syncSelectionToStore = false,
}: IUseComputeResourceDialogsOptions): IUseComputeResourceDialogsResult => {
    const dispatch = useDispatch<Dispatch>();
    const [dialog, setDialog] = React.useState(COMPUTE_RESOURCE_DIALOG.NONE);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);

    const selected = React.useMemo<IComputeResourceResponse>(
        () => items.find(item => item.id === selectedId) ?? initialComputeResourceState,
        [items, selectedId]
    );

    // The create dialog opens with nothing selected and then stays open on the CR it
    // just created, to show its installation progress and any failure. Adopt that id as
    // the selection: ComputeResourceInstall and ComputeResourceForm gate their progress
    // and error rendering on `item.id > 0`, so while the selection is still the blank
    // id-0 item the dialog shows neither (SVM2-7393). The store side already agrees —
    // createComputeResource dispatches setItem + addItem regardless of
    // syncSelectionToStore — so this only realigns the prop the dialog renders from.
    const handleCreated = setSelectedId;

    const selectInStore = (id: number) => {
        if (syncSelectionToStore) {
            dispatch(computeResourceActions.setItemId(id));
        }
    };

    const handleCreate = () => {
        setSelectedId(null);

        if (syncSelectionToStore) {
            dispatch(computeResourceActions.unsetItem());
        }

        setDialog(COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_CREATE);
    };

    const handleEdit = (id: number) => {
        setSelectedId(id);
        selectInStore(id);
        setDialog(COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_EDIT);
    };

    const handleSettings = (id: number) => {
        setSelectedId(id);
        selectInStore(id);
        setDialog(COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_SETTINGS);
    };

    const handleDetailsClick = (id: number) => {
        setSelectedId(id);
        selectInStore(id);

        if (syncSelectionToStore) {
            dispatch(computeResourceActions.getComputeResource(id));
        }

        setDialog(COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_VIEW);
    };

    const handleRemove = async (id: number) => {
        await dispatch(computeResourceActions.removeComputeResource(id, { force: false }));
        await onChanged?.();
    };

    const handleClose = () => {
        setDialog(COMPUTE_RESOURCE_DIALOG.NONE);
        setSelectedId(null);
    };

    const dialogHeading = selected.id === 0
        ? 'computeResource.actionDialog.titleAdd'
        : 'computeResource.actionDialog.titleView';

    const dialogs = (
        <>
            <Dialog
                heading={<Translate content={dialogHeading} />}
                closeHandler={handleClose}
                isOpen={dialog === COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_CREATE || dialog === COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_VIEW}
                size={SIZE.XS}
            >
                <ComputeResource onClose={handleClose} item={selected} onCreated={handleCreated} />
            </Dialog>
            <Dialog
                heading={<Translate content="computeResource.actionDialog.titleEdit" />}
                closeHandler={handleClose}
                isOpen={dialog === COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_EDIT}
                size={SIZE.XS}
            >
                <ComputeResourceEditForm onClose={handleClose} computeResource={selected} />
            </Dialog>
            <Dialog
                heading={<Translate content="computeResource.settings.form.title" />}
                closeHandler={handleClose}
                isOpen={dialog === COMPUTE_RESOURCE_DIALOG.COMPUTE_RESOURCE_SETTINGS}
                size={SIZE.XS}
            >
                <SettingsDialog onClose={handleClose} computeResource={selected} />
            </Dialog>
        </>
    );

    return {
        handleCreate,
        handleEdit,
        handleSettings,
        handleDetailsClick,
        handleRemove,
        handleClose,
        dialogs,
    };
};
